import React, { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useForm } from '@formspree/react';
import '../components/DBOSHomepage.css';
import SiteHeader from '../components/SiteHeader';
import './SmartData.css';

const SmartData = () => {
  const canvasRef = useRef(null);
  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const header = document.querySelector('.site-header');
    const subnav = document.querySelector('.sd-subnav');
    const offset = (header ? header.offsetHeight : 0) + (subnav ? subnav.offsetHeight : 0) + 24;
    const targetY = el.getBoundingClientRect().top + window.pageYOffset - offset;
    const startY = window.pageYOffset;
    const dist = targetY - startY;
    const dur = 650;
    let t0 = null;
    const easeOut = (p) => 1 - Math.pow(1 - p, 3);
    const step = (ts) => {
      if (t0 === null) t0 = ts;
      const p = Math.min((ts - t0) / dur, 1);
      window.scrollTo(0, startY + dist * easeOut(p));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };
  const [state, handleSubmit] = useForm("YOUR_FORMSPREE_ID");
  const [navOpen, setNavOpen] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(true); // Update this
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [question, setQuestion] = useState('');
  const [expandedFaq, setExpandedFaq] = useState({});

  const toggleFaq = (index) => {
    setExpandedFaq(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Opening hours logic (reuse from homepage)
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const updateOpenStatus = () => {
      const now = new Date();
      const dublinTime = new Date(now.toLocaleString("en-US", {timeZone: "Europe/Dublin"}));
      const day = dublinTime.getDay();
      const minutes = dublinTime.getHours() * 60 + dublinTime.getMinutes();
      
      const isOpenNow = (
        (day >= 1 && day <= 4 && minutes >= 540 && minutes < 1080) ||
        (day === 5 && minutes >= 540 && minutes < 720)
      );
      
      setIsOpen(isOpenNow);
      
      const indicator = document.querySelector('.status-indicator');
      const hoursText = document.querySelector('.hours-text');
      const hoursContainer = document.querySelector('.opening-hours');
      
      if (indicator && hoursText && hoursContainer) {
        if (isOpenNow) {
          indicator.className = 'status-indicator open';
          hoursText.textContent = 'OPEN';
          hoursContainer.classList.add('status-open');
          hoursContainer.classList.remove('status-closed');
        } else {
          indicator.className = 'status-indicator closed';
          hoursText.textContent = 'CLOSED';
          hoursContainer.classList.remove('status-open');
          hoursContainer.classList.add('status-closed');
        }
      }
    };

    updateOpenStatus();
    const interval = setInterval(updateOpenStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  const pricingTiers = [
    {
      name: "Starter", price: "€9.90", storage: "25 GB", aiLabel: "1,000 AI ops/mo",
      unlocks: [
        { text: "All 5 AI content packs", star: false, info: "Finance, Legal, Medical, Contacts and General — every file automatically sorted into the right category." },
        { text: "Auto-categorization", star: false, info: "Files are sorted by type (document, image, spreadsheet, presentation, code) the moment they land." },
        { text: "Location & date sorting", star: false, info: "Files are organized by upload date and folder path automatically — no manual sorting needed." },
        { text: "Basic search", star: false, info: "Search by filename, category, and upload date across your entire library." }
      ],
      benefit: "Everything you need to escape the chaos — your first 25 GB, fully organized and AI-classified."
    },
    {
      name: "Basic", price: "€19.90", storage: "60 GB", aiLabel: "3,000 AI ops/mo",
      unlocks: [
        { text: "Everything in Starter", star: false, info: "All features from the Starter plan included." },
        { text: "Bulk operations", star: true, info: "Select multiple files at once — bulk download, bulk delete, bulk move. Saves hours on large libraries." },
        { text: "Tag-based filtering", star: false, info: "Filter your library by AI-generated content tags — find all invoices, all contracts, all contact lists instantly." },
        { text: "Extracted text preview", star: false, info: "See the first lines of extracted text for documents, spreadsheets and presentations directly in the file panel." }
      ],
      benefit: "Unlocks bulk actions — select, move and manage files at scale across 60 GB."
    },
    {
      name: "Plus", price: "€29.90", storage: "100 GB", aiLabel: "8,000 AI ops/mo", popular: true,
      unlocks: [
        { text: "Everything in Basic", star: false, info: "All features from the Basic plan included." },
        { text: "Advanced filters", star: false, info: "Filter by content type, AI label, date range, file size and category simultaneously." },
        { text: "Invoice detection", star: true, info: "AI automatically detects invoices in documents and images — flags them in the Finance content pack with extra confidence." },
        { text: "100 GB data lake", star: false, info: "Your full business data lake — documents, images, spreadsheets, code, configs, all in one place." }
      ],
      benefit: "The sweet spot for an active SME — 100 GB, invoice detection, and the filters to find anything instantly."
    },
    {
      name: "Pro", price: "€49.90", storage: "200 GB", aiLabel: "20,000 AI ops/mo",
      unlocks: [
        { text: "Everything in Plus", star: false, info: "All features from the Plus plan included." },
        { text: "Semantic search", star: true, info: "Search by meaning, not just filename — find all supplier agreements from last year works even without exact keywords." },
        { text: "Smart Agent (V1.0.1)", star: true, info: "Ask questions about your files in plain language. Summarise all invoices from March or Who are my top suppliers — answered instantly." },
        { text: "200 GB data lake", star: false, info: "Double the storage of Plus — room for a growing team full document history." }
      ],
      benefit: "Ask your files questions in plain language — Smart Agent and semantic search unlock in Pro."
    },
    {
      name: "Premium", price: "€79.90", storage: "500 GB", aiLabel: "40,000 AI ops/mo",
      unlocks: [
        { text: "Everything in Pro", star: false, info: "All features from the Pro plan included." },
        { text: "Priority support", star: true, info: "Direct access to the DBOS team — faster response times and dedicated onboarding for your team." },
        { text: "500 GB storage", star: false, bright: true, info: "500 GB — room for a full multi-year business archive across every file type." },
        { text: "40,000 AI ops/month", star: false, bright: true, info: "The highest AI classification volume available — processes a large team uploads comfortably every month." }
      ],
      benefit: "Maximum capacity for serious operations — 500 GB, priority support, and 40K AI ops per month."
    }
  ];

  const faqs = [
    {
      q: "Is there really a free trial with no credit card?",
      a: "Yes! 7 days, all features unlocked. No payment info needed. After trial, choose a plan or keep 15GB free (no AI features)."
    },
    {
      q: "How does SmartData handle my privacy?",
      a: "Your files are encrypted in Google Cloud Storage (EU regions). We never train AI models on your data. GDPR-compliant, SOC 2 certified."
    },
    {
      q: "Can I cancel anytime?",
      a: "Absolutely. No contracts, no cancellation fees. Your data stays accessible for 30 days after cancellation."
    },
    {
      q: "What's the Smart Agent?",
      a: "Premium tier feature (€59.90/month). Ask questions like Show me all invoices from Q1 2026 and get a summary: 24 invoices, €45,230.50 total, 2 outstanding.",
    },
    {
      q: "Do you offer refunds?",
      a: "Yes—30-day money-back guarantee. If SmartData doesn't work for you, email hello@dbos.world for a full refund."
    },
    {
      q: "What happens when I reach my storage limit?",
      a: "You'll get a notification at 80% capacity. Upgrade to the next tier (cumulative storage) or delete old files. Files already uploaded stay accessible."
    },
    {
      q: "Can I use SmartData for my team?",
      a: "Yes! Each team member gets their own GIDO account. Shared folders coming in Q3 2026. For teams 10+, contact us for Boss Suite (full collaboration tools)."
    },
    {
      q: "What is a data migration service and do you offer such?",
      a: "A data migration service helps you transfer existing files from other platforms (Dropbox, Google Drive, OneDrive, local servers) to SmartData while preserving folder structures, metadata, and organization. Yes, we offer assisted data migration! Contact hello@dbos.world with details about your current storage setup."
    }
  ];

  useEffect(() => {
    if (!canvasRef.current) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ canvas: canvasRef.current, alpha: true, antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    camera.position.z = 50;

    const textureLoader = new THREE.TextureLoader();
    textureLoader.load(
      '/assets/img/dbos-3d-logo.png',
      (texture) => {
        const nodes = [];
        const nodeCount = 35;

        for (let i = 0; i < nodeCount; i++) {
          const spriteMaterial = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            opacity: 0.8,
          });

          const sprite = new THREE.Sprite(spriteMaterial);
          const size = 1 + Math.random() * 3;
          sprite.scale.set(size, size, 1);

          sprite.position.x = (Math.random() - 0.5) * 100;
          sprite.position.y = (Math.random() - 0.5) * 100;
          sprite.position.z = (Math.random() - 0.5) * 100;

          sprite.userData = {
            velocityX: (Math.random() - 0.5) * 0.02,
            velocityY: (Math.random() - 0.5) * 0.02,
            velocityZ: (Math.random() - 0.5) * 0.02,
            rotationSpeed: (Math.random() - 0.5) * 0.01,
          };

          scene.add(sprite);
          nodes.push(sprite);
        }

        const lineMaterial = new THREE.LineBasicMaterial({
          color: 0xd4af37,
          transparent: true,
          opacity: 0.2,
        });

        const updateLines = () => {
          scene.children.forEach((child) => {
            if (child.type === 'Line') scene.remove(child);
          });

          for (let i = 0; i < nodes.length; i++) {
            for (let j = i + 1; j < nodes.length; j++) {
              const distance = nodes[i].position.distanceTo(nodes[j].position);
              if (distance < 15) {
                const points = [nodes[i].position, nodes[j].position];
                const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
                const line = new THREE.Line(lineGeometry, lineMaterial);
                scene.add(line);
              }
            }
          }
        };

        let frameId;
        let frameCount = 0;
        const animate = () => {
          frameId = requestAnimationFrame(animate);
          frameCount++;

          nodes.forEach((node) => {
            node.position.x += node.userData.velocityX;
            node.position.y += node.userData.velocityY;
            node.position.z += node.userData.velocityZ;

            if (Math.abs(node.position.x) > 50) node.userData.velocityX *= -1;
            if (Math.abs(node.position.y) > 50) node.userData.velocityY *= -1;
            if (Math.abs(node.position.z) > 50) node.userData.velocityZ *= -1;

            node.material.rotation += node.userData.rotationSpeed;
          });

          if (frameCount % 10 === 0) updateLines();
          renderer.render(scene, camera);
        };
        animate();
      },
      undefined,
      (error) => {
        console.error('Error loading logo texture:', error);
      }
    );

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (frameId) cancelAnimationFrame(frameId);
      renderer.dispose();
    };
  }, []);

  return (
    <div className="smartdata-page">
      <canvas ref={canvasRef} className="sd-bg-canvas" />
      {/* HEADER */}
      <SiteHeader active="solutions" onSmartData={true} />
      <nav className="sd-subnav">
        <div className="sd-subnav-inner">
          <button onClick={() => scrollToSection('how-it-works')}>How It Works</button>
          <button onClick={() => scrollToSection('security')}>Security</button>
          <button onClick={() => scrollToSection('comparison')}>Comparison</button>
          <button onClick={() => scrollToSection('pricing')}>Pricing</button>
          <button onClick={() => scrollToSection('faq')}>FAQ</button>
        </div>
      </nav>

      <main className="smartdata-main">
        {/* SECTION 1: HERO */}
        <section className="smartdata-hero">
          <div className="container">
            <h1 className="hero-title shimmering">Stop Searching. Start Finding.</h1>
            <p className="hero-subtitle">Your business data, organised and classified by AI in seconds.</p>
            <p className="hero-description">
              AI-powered organization, semantic search, and smart categorization. From €9.90/month. Try free for 7 days.
            </p>
            <div className="hero-ctas">
              <a href="https://login.dbos.world/register" target="_blank" rel="noopener noreferrer" className="btn-primary shimmering-btn">
                Start Free Trial
                <span className="btn-arrow">→</span>
              </a>
              <button className="btn-secondary" onClick={() => scrollToSection('demo')}>Watch Demo</button>
            </div>
            <p className="hero-small-print">No credit card required. 7-day free trial.</p>
          </div>
        </section>

        {/* SECTION 2: THE PROBLEM */}
        <section className="content-section problem-section">
          <div className="container">
            <h2>Sound Familiar?</h2>
            <div className="problem-grid">
              <div className="problem-card">
                <h3>Chaotic Files</h3>
                <ul>
                  <li>1,000+ photos on your phone</li>
                  <li>Can't find that product shot from last month</li>
                  <li>Scrolling for 10 minutes to locate one image</li>
                </ul>
              </div>
              <div className="problem-card">
                <h3>Dumb Storage</h3>
                <ul>
                  <li>Dropbox, Google Drive: just stores files</li>
                  <li>No intelligence, no context</li>
                  <li>Search only works if you remember the filename</li>
                </ul>
              </div>
              <div className="problem-card">
                <h3>Wasted Time</h3>
                <ul>
                  <li>2 hours/week searching for files</li>
                  <li>Duplicate uploads (forgot you already had it)</li>
                  <li>Missed deadlines because you couldn't find the invoice</li>
                </ul>
            </div>
            </div>
            <div className="qa-block">
              <p className="qa-question"><strong>Q: What's the difference between "dumb" storage and "smart" storage?</strong></p>
              <p className="qa-answer">A: Dumb storage (Dropbox, Google Drive) just stores files by name. SmartData uses AI to understand what's IN your files—photos of guitars get labeled "guitar," invoices get categorized automatically, and you can search by content, not just filenames.</p>
            </div>
          </div>
        </section>

        {/* SECTION 3: THE SOLUTION */}
        <section className="content-section solution-section">
          <div className="container">
            <h2>Meet SmartData: Storage That Thinks</h2>
            <p className="section-subtitle">Three steps to intelligent file management</p>
            
            <div className="solution-steps">
              <div className="solution-step">
                <div className="step-number">1</div>
                <h3>Upload Your Files</h3>
                <p>Drag and drop images, documents, invoices—anything.</p>
              </div>
              <div className="solution-step">
                <div className="step-number">2</div>
                <h3>AI Does the Work</h3>
                <ul>
                  <li>Auto-categorization by type and date</li>
                  <li>Image labeling (Cloud Vision AI)</li>
                  <li>OCR for invoices and receipts</li>
                  <li>Location-based sorting (EXIF data)</li>
                </ul>
              </div>
              <div className="solution-step">
                <div className="step-number">3</div>
                <h3>Find Anything Instantly</h3>
                <p>Search "wooden instruments" → finds all guitar photos<br/>
                Search "invoices from Q1" → Smart Agent summarizes totals</p>
              </div>
            </div>

            <a href="https://login.dbos.world/register" target="_blank" rel="noopener noreferrer" className="btn-primary shimmering-btn">
              Try SmartData Free for 7 Days
              <span className="btn-arrow">→</span>
            </a>

            <div className="qa-block">
              <p className="qa-question"><strong>Q: How does AI categorization work?</strong></p>
              <p className="qa-answer">A: When you upload a photo of a guitar, SmartData's AI (Google Cloud Vision) detects "acoustic guitar," "musical instrument," "wood finish" and tags it automatically. You can then search "guitars" or even "wooden instruments" and find it instantly—even if the filename was IMG_0742.jpg.</p>
            </div>
          </div>
        </section>

                {/* SECTION 3b: DATA SECURITY (early trust) */}
        <section id="security" className="content-section">
          <div className="container">
            <h2>Your Data, Protected from Day One</h2>
            <p className="section-subtitle">Enterprise-grade security and EU data residency — built in, on every plan.</p>
            <div className="sd-security">
              <div className="sd-sec-card">
                <h3>EU Data Residency</h3>
                <p>All files are stored in Google Cloud Storage buckets in EU regions (europe-west1 Ireland, europe-west3 Germany). GDPR-compliant, encrypted at rest and in transit.</p>
              </div>
              <div className="sd-sec-card">
                <h3>Enterprise Security, SME Pricing</h3>
                <p>Bank-level encryption and SOC 2 compliant infrastructure. Fortune 500 security — from €9.90/month.</p>
              </div>
              <div className="sd-sec-card">
                <h3>European Excellence</h3>
                <p>Built in Ireland, deployed globally. GDPR-compliant by design — your data stays in EU data centers.</p>
              </div>
            </div>
          </div>
        </section>
        {/* SECTION 4: KEY FEATURES */}
        <section className="content-section features-section">
          <div className="container">
            <h2>Three Features That Change Everything</h2>
            
            <div className="features-grid">
              <div className="feature-card">
                <h3>AI Image Labeling</h3>
                <p>Understands photo content, not just filenames. Search "sunset Dublin" → finds photos by location + content.</p>
                <p className="feature-tier">Available from Basic tier (€19.90/month)</p>
              </div>
              <div className="feature-card">
                <h3>Invoice Detection</h3>
                <p>Photograph paper invoices with your phone. AI extracts vendor, date, amount automatically. Searchable by vendor, date, or amount.</p>
                <p className="feature-tier">Available from Plus tier (€29.90/month)</p>
              </div>
              <div className="feature-card">
                <h3>Smart Agent</h3>
                <p>Natural language queries: "Summarize my Q1 invoices" → Returns: "24 invoices, €45,230.50 total, 2 outstanding"</p>
                <p className="feature-tier">Available in Premium tier (€59.90/month)</p>
              </div>
            </div>

            <div className="qa-block">
              <p className="qa-question"><strong>Q: Can SmartData read text from scanned documents?</strong></p>
              <p className="qa-answer">A: Yes! From the Plus tier onwards, SmartData uses OCR (Optical Character Recognition) to extract text from images and PDFs. If you photograph a paper invoice, it will detect the invoice number, vendor, amount, and due date automatically.</p>
            </div>
          </div>
        </section>

        
        {/* VIDEO SECTION */}
        <section id="demo" className="content-section video-section">
          <div className="container">
            <h2>See SmartData in Action</h2>
            <p className="section-subtitle">Watch how SmartData transforms your file chaos into organized intelligence.</p>
            
            <div className="video-embed-wrap">
              <iframe
                src="https://www.youtube.com/embed/gOP1NpYRBLM"
                title="SmartData by DBOS | AI That Organizes Your Business Files"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </section>

        {/* SECTION 7: COMPARISON TABLE */}
        <section id="comparison" className="content-section comparison-section">
          <div className="container">
            <h2>SmartData vs. The Competition</h2>
            <p className="section-subtitle">Feature comparison (pricing excluded)</p>
            
            <div className="comparison-table-wrapper">
              <table className="comparison-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>SmartData</th>
                    <th>Dropbox</th>
                    <th>Google Drive</th>
                    <th>OneDrive</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>AI Categorization</td>
                    <td className="check">✅ Auto</td>
                    <td className="cross">❌ Manual</td>
                    <td className="cross">❌ Manual</td>
                    <td className="cross">❌ Manual</td>
                  </tr>
                  <tr>
                    <td>Image AI Labeling</td>
                    <td className="check">✅ Yes</td>
                    <td className="cross">❌ No</td>
                    <td className="warning">⚠️ Limited</td>
                    <td className="cross">❌ No</td>
                  </tr>
                  <tr>
                    <td>OCR Invoice Detection</td>
                    <td className="check">✅ Yes</td>
                    <td className="cross">❌ No</td>
                    <td className="warning">⚠️ Limited</td>
                    <td className="cross">❌ No</td>
                  </tr>
                  <tr>
                    <td>Smart Agent (NLU Queries)</td>
                    <td className="check">✅ Premium</td>
                    <td className="cross">❌ No</td>
                    <td className="cross">❌ No</td>
                    <td className="cross">❌ No</td>
                  </tr>
                  <tr>
                    <td>Semantic Search</td>
                    <td className="check">✅ Pro+</td>
                    <td className="cross">❌ Filename only</td>
                    <td className="warning">⚠️ Limited</td>
                    <td className="cross">❌ Filename only</td>
                  </tr>
                  <tr>
                    <td>GDPR Compliance</td>
                    <td className="check">✅ EU data residency</td>
                    <td className="check">✅ Yes</td>
                    <td className="check">✅ Yes</td>
                    <td className="check">✅ Yes</td>
                  </tr>
                  <tr>
                    <td>Boss Suite Upgrade Path</td>
                    <td className="check">✅ Seamless</td>
                    <td className="cross">❌ No</td>
                    <td className="cross">❌ No</td>
                    <td className="cross">❌ No</td>
                  </tr>
                  <tr>
                    <td>Custom Branding (Own CI)</td>
                    <td className="check">✅ All tiers</td>
                    <td className="cross">❌ Enterprise only</td>
                    <td className="cross">❌ No</td>
                    <td className="cross">❌ No</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="comparison-callout">
              <p><strong>The Big Difference:</strong> Competitors store files. SmartData understands them.</p>
            </div>

            <a href="https://login.dbos.world/register" target="_blank" rel="noopener noreferrer" className="btn-primary shimmering-btn">
              See SmartData in Action - Start Free Trial
              <span className="btn-arrow">→</span>
            </a>

            <div className="qa-block">
              <p className="qa-question"><strong>Q: What does "semantic search" mean?</strong></p>
              <p className="qa-answer">A: Traditional search looks for exact keywords. Semantic search understands meaning. Search "wooden instruments" and SmartData finds photos tagged "guitar," "violin," "cello"—even if those exact words aren't in the filename.</p>
            </div>
            <div className="qa-block">
              <p className="qa-question"><strong>Q: Can I migrate from Dropbox or Google Drive?</strong></p>
              <p className="qa-answer">A: Yes! Upload your existing files and SmartData will auto-categorize them. Bulk upload available from 50GB onwards (Layer I unlock).</p>
            </div>
          </div>
        </section>

        {/* SECTION 5: PRICING */}
        <section id="pricing" className="content-section pricing-section">
          <div className="container">
            <h2>Choose Your Plan</h2>
            <p className="pricing-intro">Every plan includes all AI categories — Finance, Legal, Medical, Contacts, General. The difference is volume, not capability.</p>
            <p className="section-subtitle">All plans include 7-day free trial. No credit card required.</p>
            
            <div className="sd-pricing-grid">
              {pricingTiers.map((tier, index) => (
                <div key={index} className={`sd-tier ${tier.popular ? 'popular' : ''}`}>
                  {tier.popular && <span className="sd-tier-badge">Most popular</span>}
                  <div className="sd-tier-name">{tier.name}</div>
                  <div className="sd-tier-price">{tier.price}<span>/mo</span></div>
                  <div className="sd-tier-spec">{tier.storage} storage</div>
                  <div className="sd-tier-spec">{tier.aiLabel}</div>
                  <ul className="sd-tier-unlocks">
                    {tier.unlocks.map((u, i) => (
                      <li key={i} className={`${u.star ? 'is-star' : ''} ${u.bright ? 'is-bright' : ''}`}>
                        {u.star ? '✦ ' : ''}{u.text}
                        {u.info && (
                          <span className="sd-info-wrap">
                            <span className="sd-info-icon">ⓘ</span>
                            <span className="sd-info-tip">{u.info}</span>
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                  <div className="sd-tier-benefit">{tier.benefit}</div>
                  <div className="sd-cta-wrap">
                    <a href="https://login.dbos.world/register" className="sd-tier-cta" target="_blank" rel="noopener noreferrer">Start Free Trial</a>
                    <span className="sd-cta-info">ⓘ
                      <span className="sd-cta-tip">The 7-day free trial is identical on every plan — all features unlocked. You don't choose a plan now; pick or change it any time later. No commitment, no card required.</span>
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="pricing-callout">
              <p><strong>Most Popular:</strong> Plus Tier (€29.90) - Perfect for SMEs with 100+ invoices/documents per month.</p>
            </div>

            <div className="qa-block">
              <p className="qa-question"><strong>Q: What happens after the 7-day trial?</strong></p>
              <p className="qa-answer">A: You choose a paid plan or your account remains free with 15GB storage (no AI features). Your files stay safe—we never delete your data.</p>
            </div>
            <div className="qa-block">
              <p className="qa-question"><strong>Q: Can I upgrade or downgrade anytime?</strong></p>
              <p className="qa-answer">A: Yes! Change plans anytime from your dashboard. Storage is cumulative (Starter 25GB + Basic upgrade = 60GB total).</p>
            </div>
          </div>
        </section>

        {/* SECTION 6: WHY CHOOSE DBOS */}
        <section className="content-section why-dbos-section">
          <div className="container">
            <h2>Why Influencers & SME Clients Trust Us</h2>
            
            <div className="why-grid">
              <div className="why-card">
                <h3>Infrastructure as Code</h3>
                <p>Deployed via OpenTofu automation. Your territory provisions in 10 minutes. Scalable from day one.</p>
              </div>
              <div className="why-card">
                <h3>Real Human Support</h3>
                <p>Email, phone, or video call. Talk to actual developers, not chatbots. Avg response time: 4 hours.</p>
              </div>
              <div className="why-card">
                <h3>Global Availability</h3>
                <p>Active territories: Ireland, Germany, UK, Cyprus. Coming soon: France, Spain, Italy, Netherlands.</p>
              </div>
              <div className="why-card">
                <h3>Future-Proof Platform</h3>
                <p>SmartData is Layer 0 of the DBOS ecosystem. When you grow, upgrade seamlessly to Boss Suite (full automation, workflows, ERP). No data migration needed.</p>
              </div>
            </div>

            <div className="qa-block">
              <p className="qa-question"><strong>Q: Can I talk to a real person if I need help?</strong></p>
              <p className="qa-answer">A: Absolutely! Email hello@dbos.world or request a call via your dashboard. We're a startup—you'll talk to the people who built the product.</p>
            </div>
          </div>
        </section>

        {/* SECTION 8: HOW IT WORKS */}
        <section id="how-it-works" className="content-section how-it-works-section">
          <div className="container">
            <h2>Get Started in 3 Minutes</h2>
            
            <div className="how-steps">
              <div className="how-step">
                <div className="step-number">1</div>
                <h3>Create Your Account</h3>
                <ul>
                  <li>Register at dbos.world/register</li>
                  <li>Verify email, set up 2FA</li>
                  <li>7-day free trial activated instantly</li>
                </ul>
              </div>
              <div className="how-step">
                <div className="step-number">2</div>
                <h3>Upload Your Files</h3>
                <ul>
                  <li>Drag and drop photos, invoices, documents</li>
                  <li>AI starts categorizing immediately</li>
                  <li>Progress tracker shows processing status</li>
                </ul>
              </div>
              <div className="how-step">
                <div className="step-number">3</div>
                <h3>Search and Find</h3>
                <ul>
                  <li>Use the search bar or Smart Agent</li>
                  <li>Find files by content, not filenames</li>
                  <li>Export, share, or download anytime</li>
                </ul>
              </div>
            </div>

            <a href="https://login.dbos.world/register" target="_blank" rel="noopener noreferrer" className="btn-primary shimmering-btn">
              Start Free Trial - No Credit Card Required
              <span className="btn-arrow">→</span>
            </a>
          </div>
        </section>

        {/* SECTION 9: FAQ */}
        <section id="faq" className="content-section faq-section">
          <div className="container">
            <h2>Frequently Asked Questions</h2>
            
            <div className="faq-accordion">
              {faqs.map((faq, index) => (
                <div key={index} className="faq-item">
                  <div className="faq-question" onClick={() => toggleFaq(index)}>
                    <h3>{faq.q}</h3>
                    <span className="faq-toggle">{expandedFaq[index] ? '−' : '+'}</span>
                  </div>
                  {expandedFaq[index] && (
                    <div className="faq-answer">
                      <p>{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 10: FINAL CTA */}
        <section className="content-section final-cta-section">
          <div className="container">
            <h2>Join 100+ Businesses Already Using SmartData</h2>
            <p className="section-subtitle">Start your 7-day free trial today. No credit card required.</p>
            
            <div className="testimonials-row">
              <div className="testimonial-quote">
                <p>"I photograph every invoice with my phone. SmartData extracts the amounts automatically. My accountant loves me now."</p>
                <p className="testimonial-author">— Sarah M., Café Colomba (Munich)</p>
              </div>
              <div className="testimonial-quote">
                <p>"We had 5,000 product photos with names like IMG_0482.jpg. SmartData labeled them all in 2 days. Game changer."</p>
                <p className="testimonial-author">— Glen Salmon, Essaness Music (Kilkenny)</p>
              </div>
            </div>

            <div className="trust-badges">
              <span className="badge">GDPR Compliant</span>
              <span className="badge">SOC 2 Certified</span>
              <span className="badge">EU Data Residency</span>
              <span className="badge">30-Day Money-Back</span>
            </div>

            <a href="https://login.dbos.world/register" target="_blank" rel="noopener noreferrer" className="btn-primary shimmering-btn btn-large">
              Start Your Free Trial Now
              <span className="btn-arrow">→</span>
            </a>
            
            <p className="cta-small-print">7 days free. No credit card. All features unlocked.</p>
          </div>
        </section>

        {/* SECTION 11: CONTACT */}
        <section className="content-section contact-section">
          <div className="container">
            <h2>Questions? We're Here to Help</h2>
            
            <div className="contact-options">
              <p><strong>Email:</strong> hello@dbos.world</p>
              <p><strong>Call:</strong> +353 (0)89 22 77 133</p>
            </div>

            <div className="contact-form-wrapper">
              <h3>Or submit a question:</h3>
              <form className="contact-form" onSubmit={handleSubmit}>
                <input
                  type="text"
                  name="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your Name"
                  required
                  className="form-input"
                />
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your Email"
                  required
                  className="form-input"
                />
                <textarea
                  name="question"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Your Question"
                  required
                  className="form-textarea"
                  rows="5"
                />
                <button type="submit" className="btn-primary shimmering-btn" disabled={state.submitting}>
                  Send Message
                  <span className="btn-arrow">→</span>
                </button>
                {state.succeeded && <p className="form-success">Message sent! We'll respond within 4 hours.</p>}
              </form>
              <p className="response-time">We typically respond within 4 hours (Mon-Fri, 9am-6pm CET).</p>
            </div>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="footer">
        <div className="container footer-compact">
          <div className="footer-row-1">
            <span className="footer-hours-inline">
              <span className="hours-label">Opening Hours (IST):</span> Mon-Thu 9:00-18:00 | Fri 9:00-12:00 | Sat-Sun Closed
            </span>
          </div>
          <div className="footer-row-2">
            <p className="footer-copyright">© 2026 DBOS | CRO No. 778363</p>
            <p className="footer-legal">
              <a href="/legal/terms">Terms</a> | <a href="/legal/privacy">Privacy Policy</a> | 
              <a href="/legal/cookies">Cookies</a> | <a href="/legal/dpa">DPA</a>
            </p>
            <p className="footer-email">
              <a href="mailto:hello@dbos.world">hello@dbos.world</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SmartData;
